import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { api, ApiError, type LicenseKeyEventEntry } from "../api.js";
import { useToken } from "../auth.js";
import { Badge } from "../components/Badge.js";

// Timeline of agent-initiated license-key rotations. Rows are what
// license_key_events records: a `swap_check` row for every pre-flight probe
// the local app sent to the cloud, and a `swap_commit` row when the machine
// actually re-registered under the new licence.

type OutcomeFilter = "" | "approved" | "rejected";
type ActionFilter = "" | "swap_check" | "swap_commit";

export function LicenseSwaps() {
  const token = useToken();
  const [entries, setEntries] = useState<LicenseKeyEventEntry[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string>("");
  const [toLicenseId, setToLicenseId] = useState<string>("");
  const [outcome, setOutcome] = useState<OutcomeFilter>("");
  const [action, setAction] = useState<ActionFilter>("");
  const [offset, setOffset] = useState(0);
  const limit = 100;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.licenseKeyEvents(token, {
        customerId: customerId || undefined,
        toLicenseId: toLicenseId || undefined,
        outcome: outcome || undefined,
        action: action || undefined,
        limit,
        offset,
      });
      setEntries(res.entries);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token, customerId, toLicenseId, outcome, action, offset]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const { t } = useLingui();

  return (
    <div>
      <header className="page-header">
        <h1><Trans>License swaps</Trans></h1>
        <div className="row-actions">
          <label>
            <Trans>Customer ID</Trans>{" "}
            <input
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                setOffset(0);
              }}
              placeholder={t`uuid`}
            />
          </label>
          <label>
            <Trans>License ID (target)</Trans>{" "}
            <input
              value={toLicenseId}
              onChange={(e) => {
                setToLicenseId(e.target.value);
                setOffset(0);
              }}
              placeholder={t`uuid`}
            />
          </label>
          <label>
            <Trans>Outcome</Trans>{" "}
            <select
              value={outcome}
              onChange={(e) => {
                setOutcome(e.target.value as OutcomeFilter);
                setOffset(0);
              }}
            >
              <option value="">{t`(any)`}</option>
              <option value="approved">{t`approved`}</option>
              <option value="rejected">{t`rejected`}</option>
            </select>
          </label>
          <label>
            <Trans>Action</Trans>{" "}
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value as ActionFilter);
                setOffset(0);
              }}
            >
              <option value="">{t`(any)`}</option>
              <option value="swap_check">{t`swap_check`}</option>
              <option value="swap_commit">{t`swap_commit`}</option>
            </select>
          </label>
          <button className="ghost" onClick={() => void refresh()}>
            <Trans>Refresh</Trans>
          </button>
        </div>
      </header>
      {error && <div className="banner err">{error}</div>}
      {loading ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : (
        <>
          <p className="muted">
            <Trans>
              {total} total entries — showing {offset + 1}–{Math.min(offset + entries.length, total)}
            </Trans>
          </p>
          <table className="table">
            <thead>
              <tr>
                <th><Trans>When (UTC)</Trans></th>
                <th><Trans>Customer</Trans></th>
                <th><Trans>Target license</Trans></th>
                <th><Trans>Action</Trans></th>
                <th><Trans>Outcome</Trans></th>
                <th><Trans>From → To</Trans></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>
                    {new Date(e.createdAt).toISOString().replace("T", " ").slice(0, 19)}
                  </td>
                  <td>
                    {e.customerId && e.customerName ? (
                      <Link to={`/customers/${e.customerId}`}>{e.customerName}</Link>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {e.toLicenseLabel ?? (
                      <code>…{e.toLicenseKeyLast4}</code>
                    )}
                  </td>
                  <td><code>{e.action}</code></td>
                  <td>
                    <Badge tone={e.outcome === "approved" ? "ok" : "err"}>
                      {e.outcome}
                    </Badge>
                    {e.reason && (
                      <div className="muted" style={{ fontSize: "0.85em" }}>
                        {e.reason}
                      </div>
                    )}
                  </td>
                  <td>
                    <code>
                      {e.fromLicenseKeyLast4 ? `…${e.fromLicenseKeyLast4}` : "—"}
                      {" → "}
                      …{e.toLicenseKeyLast4}
                    </code>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ textAlign: "center" }}>
                    <Trans>No license swaps match.</Trans>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="row-actions" style={{ marginTop: "1rem" }}>
            <button
              className="ghost"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            >
              <Trans>← Newer</Trans>
            </button>
            <button
              className="ghost"
              disabled={offset + entries.length >= total}
              onClick={() => setOffset(offset + limit)}
            >
              <Trans>Older →</Trans>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
